/**
 * The project's chats: which thread is open, what the list says about each, and
 * whether the open one is mid-turn.
 *
 * Two panes read this — the file pane lists the threads, the builder pane runs
 * the open one — so the selection can't live in either. `busy` is reported UP
 * from the builder (it owns the feed socket) because the checkpoint rail in the
 * file pane has to lock while a turn is writing to the project.
 *
 * `open` is also what decides whether the builder pane exists at all. There is
 * no separate "is the chat sidebar showing" flag, because there is nothing a
 * chat sidebar could show with no chat in it: a project has many threads, and
 * an empty pane parked open is a slot waiting to be told which one it means.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { invariant } from "ts-invariant";

/** The chats list row, as `GET /api/chats` returns it. */
export interface Chat {
  id: string;
  title: string | null;
  preview: string | null;
  model_id: string;
  updated_at: string;
  /** The task this conversation works in; `null` is main. It can move. */
  task_id: string | null;
  task_title: string | null;
  task_status: "open" | "applied" | "completed" | "archived" | null;
}

/** One conversation's row, wherever it falls in the list. */
export async function fetchChat(chatId: string): Promise<Chat> {
  const res = await fetch(`/api/chats/${chatId}`);
  if (!res.ok) throw new Error(`chat read failed (${res.status})`);
  return (await res.json()) as Chat;
}

async function fetchChats(projectId: string): Promise<Chat[]> {
  const res = await fetch(`/api/chats?projectId=${projectId}`);
  if (!res.ok) throw new Error(`chat list failed (${res.status})`);
  return (await res.json()) as Chat[];
}

/** A new conversation, on main or in the open task the member is viewing. */
export async function createChat(
  projectId: string,
  modelId: string,
  taskId?: string,
): Promise<Chat> {
  const res = await fetch("/api/chats", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ projectId, modelId, taskId }),
  });
  if (!res.ok) throw new Error(`chat create failed (${res.status})`);
  return (await res.json()) as Chat;
}

/**
 * What the builder is showing. A `draft` has no row in the list yet — the model
 * picker is live and the first send creates it. `null` is the builder closed,
 * which is a state a boolean flag beside an id could not tell apart from "the
 * pane is open on a draft".
 */
export type OpenChat =
  | { kind: "thread"; id: string }
  | { kind: "draft" }
  | null;

export interface ProjectChats {
  list: Chat[];
  open: OpenChat;
  /** Show an existing thread. */
  openThread: (chatId: string) => void;
  /** Start a thread that does not exist yet. */
  startDraft: () => void;
  /** Dismiss the builder. The thread is untouched; nothing is showing it. */
  close: () => void;
  /**
   * Bumped whenever a chat is opened. The builder watches it and puts the
   * cursor in the composer: opening a thread — a new one most of all — is a
   * member saying they are about to type. Starts at 0, which is "nobody has
   * asked", so the composer doesn't steal focus on load.
   */
  focusRequest: number;
  /** Take up the row a draft's first send just created. */
  adopt: (chat: Chat) => void;
  /** Re-read the list, so titles and previews track the settle envelope. */
  /** Resolves once the list on screen is the server's. */
  refresh: () => Promise<void>;
  /** A turn is running. What locks the checkpoint rail. */
  busy: boolean;
  setBusy: (busy: boolean) => void;
}

export function useProjectChats(projectId: string): ProjectChats {
  const [list, setList] = useState<Chat[]>([]);
  const [open, setOpen] = useState<OpenChat>(null);
  const [busy, setBusy] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);

  // Loading the list does not open anything: which thread a member wants is
  // theirs to say, and guessing the newest one puts a conversation on screen
  // that nobody asked for.
  useEffect(() => {
    let cancelled = false;
    void fetchChats(projectId).then((chats) => {
      if (!cancelled) setList(chats);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const refresh = useCallback(
    () => fetchChats(projectId).then(setList),
    [projectId],
  );

  const show = useCallback((next: OpenChat) => {
    setOpen(next);
    setFocusRequest((n) => n + 1);
  }, []);

  const openThread = useCallback(
    (id: string) => show({ kind: "thread", id }),
    [show],
  );
  const startDraft = useCallback(() => show({ kind: "draft" }), [show]);
  const close = useCallback(() => setOpen(null), []);

  const adopt = useCallback((chat: Chat) => {
    setList((prev) =>
      prev.some((c) => c.id === chat.id) ? prev : [chat, ...prev],
    );
    setOpen({ kind: "thread", id: chat.id });
  }, []);

  return {
    list,
    open,
    openThread,
    startDraft,
    close,
    focusRequest,
    adopt,
    refresh,
    busy,
    setBusy,
  };
}

/**
 * The project's chats, owned by the project shell so the open conversation
 * stays open as the member moves between views and branches.
 */
export const ProjectChatsContext = createContext<ProjectChats | null>(null);

export function useSharedProjectChats(): ProjectChats {
  const chats = useContext(ProjectChatsContext);
  invariant(chats, "useSharedProjectChats must be inside the project shell");
  return chats;
}
